import {
  GcpBox,
  GcpTypography,
  RequestRefundDialog,
  TransactionStatus,
  VoidDialog,
} from '@back-office/ui';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { REFUND_TYPE, VOID_TYPE } from '../../constants';
import RefundedTransaction from '../transaction-detail/RefundTransaction';
import useCardStyles from '../transaction-detail/styles';
import TransactionAmount from '../transaction-detail/TransactionAmount';
import TransactionBreadcrumbs from '../transaction-detail/TransactionBreadcrumbs';
import TransactionDetailCard from '../transaction-detail/TransactionDetailCard';
import TransactionFee from '../transaction-detail/TransactionFee';
import TransactionLogs from '../transaction-detail/TransactionLogs';
import TransactionTransfer from '../transaction-detail/TransactionTransfer';
import TransactionVoid from '../transaction-detail/TransactionVoid';
import VoidedTransaction from '../transaction-detail/VoidedTransaction';
import useViewModel from './viewmodel';

interface TransactionDetailProps {
  txId: string | null;
  handleOnClickBreadcrumbs: () => void;
  query: string;
}

const TransactionDetail: FC<TransactionDetailProps> = observer((props) => {
  const { txId, handleOnClickBreadcrumbs } = props;
  const classes = useCardStyles();
  const { t } = useTranslation();
  const {
    voidDialog,
    toggleVoidDialog,
    requestRefundDialog,
    toggleRequestRefundDialog,
    isTxVoidable,
    isTxRefundPending,
    isTxRefundable,
    isVoidRefundPermission,
    statusLabel,
    handleVoid,
    handleRequestRefund,
    handleCancelRefund,
    onClickRequestRefund,
    sortAndFilterRequestRefundTypes,
    errorMessage,
    isAdmin,
    requestTypes,
    detail,
    getTransactionDetail,
    alertMessage,
    setAlertMessage,
  } = useViewModel(txId);

  return (
    <GcpBox>
      <TransactionBreadcrumbs
        handleOnClickBreadcrumbs={handleOnClickBreadcrumbs}
      />
      <GcpBox mt={3}>
        <Divider />
      </GcpBox>
      <TransactionVoid
        id={detail.id}
        showCancelBtn={(isAdmin || isVoidRefundPermission) && isTxRefundPending}
        showRequestBtn={(isAdmin || isVoidRefundPermission) && isTxRefundable}
        showVoidBtn={(isAdmin || isVoidRefundPermission) && isTxVoidable}
        transactionID={detail.transactionID}
        updatedAt={detail.updatedAt}
        updatedBy={detail.updatedBy}
        onCancelClick={handleCancelRefund}
        onRequestClick={() => onClickRequestRefund()}
        onVoidClick={() => toggleVoidDialog(true)}
      />
      {alertMessage && (
        <Alert
          className={classes.successMessage}
          icon={false}
          severity="success"
        >
          <GcpTypography variant="body1">{alertMessage}</GcpTypography>
        </Alert>
      )}
      <TransactionAmount
        amount={detail.amount}
        currency={detail.currency}
        isVoided={detail?.child?.type === VOID_TYPE}
        statusLabel={statusLabel}
        transferAmount={detail.transferAmount}
      />
      <TransactionDetailCard
        createdAt={detail.createdAt}
        description={detail.description}
        id={detail.id}
        qrType={detail.qrType || ''}
        ref1={detail.ref1}
        ref2={detail.ref2 || ''}
        refresh={() => getTransactionDetail(txId || '')}
        status={detail.status}
        transactionID={detail.transactionID}
        txId={txId || ''}
        onUpdated={() => {
          setAlertMessage(t('transaction_list.update_success_message'));
        }}
      />
      <TransactionTransfer
        fundsUID={detail.fundsUID}
        merchantBusinessType={detail.merchantBusinessType}
        mid={detail.mid}
        payerName={detail.payerName}
        sourceOfFund={detail.sourceOfFund?.name}
        tid={detail.tid}
      />
      {detail.status !== TransactionStatus.Confirming && (
        <TransactionFee
          amount={detail.amount}
          calFeeAmount={detail.calFeeAmount}
          currency={detail.currency}
          transferAmount={detail.transferAmount}
        />
      )}
      {detail?.child?.type === VOID_TYPE && (
        <VoidedTransaction
          amount={detail.child.amount}
          currency={detail.currency}
          id={detail.child.id}
        />
      )}
      {detail?.child?.type === REFUND_TYPE && (
        <RefundedTransaction
          amount={detail.child.amount}
          currency={detail.currency}
          id={detail.child.id}
        />
      )}
      <TransactionLogs auditTrail={detail.auditTrail} />
      <VoidDialog
        amount={parseFloat(detail.amount)}
        open={voidDialog}
        onCancel={() => toggleVoidDialog(false)}
        onSubmit={handleVoid}
      />
      <RequestRefundDialog
        amount={parseFloat(detail.amount)}
        error={errorMessage}
        open={requestRefundDialog}
        requestTypes={sortAndFilterRequestRefundTypes(requestTypes.list)}
        onCancel={() => toggleRequestRefundDialog(false)}
        onSubmit={handleRequestRefund}
      />
    </GcpBox>
  );
});

export default TransactionDetail;
